/**
 * Coluna ESQUERDA do /vault (280px): '+ Novo cofre' e a lista de cofres a que
 * tenho acesso (nome, nº de senhas, chip do meu nível Leitura/Edição).
 * Hover: editar/excluir — apenas com nível Edição.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import type { Vault } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { errorMessage, myVaultsOptions } from './vault-queries';
import { entryCountLabel, LevelBadge } from './vault-format';
import { DeleteVaultDialog, EditVaultDialog, NewVaultDialog } from './vault-dialogs';

export function VaultListPane({
  selectedId,
  onSelect,
}: {
  selectedId: number | null;
  onSelect: (vaultId: number | null) => void;
}) {
  const vaultsQ = useQuery(myVaultsOptions());
  const vaults = vaultsQ.data ?? [];

  const [newOpen, setNewOpen] = useState(false);
  const [editVault, setEditVault] = useState<Vault | null>(null);
  const [deleteVault, setDeleteVault] = useState<Vault | null>(null);

  // Auto-seleção do primeiro cofre quando não há seleção válida (inclui o
  // caso do cofre selecionado ter sido excluído).
  useEffect(() => {
    if (vaultsQ.data === undefined) return;
    const exists = selectedId !== null && vaultsQ.data.some((v) => v.id === selectedId);
    if (!exists) onSelect(vaultsQ.data[0]?.id ?? null);
  }, [vaultsQ.data, selectedId, onSelect]);

  return (
    <div className="flex w-[280px] shrink-0 flex-col overflow-hidden rounded-lg border bg-card shadow-xs">
      <div className="shrink-0 border-b p-3">
        <Button size="sm" className="w-full" onClick={() => setNewOpen(true)}>
          <Plus className="size-4" strokeWidth={2.5} />
          Novo cofre
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {vaultsQ.isPending ? (
          <div aria-busy className="divide-y divide-divider">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="space-y-2 px-3 py-3">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        ) : vaultsQ.isError ? (
          <EmptyState message={`Erro ao carregar cofres: ${errorMessage(vaultsQ.error)}`} />
        ) : vaults.length === 0 ? (
          <EmptyState message="Nenhum cofre — crie o primeiro" />
        ) : (
          <ul className="divide-y divide-divider">
            {vaults.map((vault) => (
              <li key={vault.id} className="group relative">
                <button
                  type="button"
                  onClick={() => onSelect(vault.id)}
                  aria-current={vault.id === selectedId || undefined}
                  className={cn(
                    'flex w-full flex-col gap-1 px-3 py-2.5 text-left transition-colors',
                    vault.id === selectedId ? 'bg-accent' : 'hover:bg-accent/50'
                  )}
                >
                  <span className="flex items-center gap-2 pr-12">
                    <Lock className="size-3.5 shrink-0 text-muted-foreground" />
                    <span className="truncate text-sm font-semibold">{vault.name}</span>
                  </span>
                  <span className="flex items-center justify-between gap-2 pl-[22px]">
                    <span className="text-xs text-muted-foreground">
                      {entryCountLabel(vault.entryCount)}
                    </span>
                    <LevelBadge level={vault.myLevel} className="px-1.5 py-0 text-[10px]" />
                  </span>
                </button>

                {vault.myLevel === 'write' ? (
                  <span className="absolute top-1.5 right-1.5 flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      title="Editar cofre"
                      aria-label={`Editar cofre ${vault.name}`}
                      className="text-muted-foreground hover:text-primary"
                      onClick={() => setEditVault(vault)}
                    >
                      <Pencil />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      title="Excluir cofre"
                      aria-label={`Excluir cofre ${vault.name}`}
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => setDeleteVault(vault)}
                    >
                      <Trash2 />
                    </Button>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {newOpen ? (
        <NewVaultDialog
          onCreated={(vaultId) => onSelect(vaultId)}
          onClose={() => setNewOpen(false)}
        />
      ) : null}
      {editVault !== null ? (
        <EditVaultDialog vault={editVault} onClose={() => setEditVault(null)} />
      ) : null}
      {deleteVault !== null ? (
        <DeleteVaultDialog vault={deleteVault} onClose={() => setDeleteVault(null)} />
      ) : null}
    </div>
  );
}
